import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Modal, Select, Spin } from "antd";
import { GET_AGENT_BUYERS } from "../graphql/agentQueries";
import {
  ADD_TO_AGENT_FAVORITES,
  ADD_TO_BUYER_FAVORITES,
} from "../graphql/favoriteQueries";
import { useToast } from "./Toast/toastHook";

const FavoriteModal = ({
  isOpen,
  onClose,
  listingId,
  agentId,
  companyId,
  favoriteStatus,
  onRemoveAgentFavorite,
  onRemoveBuyerFavorite,
}) => {
  const [selectedTab, setSelectedTab] = useState("my"); // "my" or "buyer"
  const [selectedBuyerId, setSelectedBuyerId] = useState(null);
  const toast = useToast(4000);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedTab("my");
      setSelectedBuyerId(null);
    }
  }, [isOpen]);

  // Fetch agent's buyers
  const { data: buyersData, loading: buyersLoading } = useQuery(GET_AGENT_BUYERS, {
    variables: { userId: agentId },
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const [addToAgentFavorites, { loading: addingAgent }] = useMutation(ADD_TO_AGENT_FAVORITES, {
    onCompleted() {
      toast("success", "Added to your favorites!");
      onClose();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const [addToBuyerFavorites, { loading: addingBuyer }] = useMutation(ADD_TO_BUYER_FAVORITES, {
    onCompleted() {
      toast("success", "Added to buyer's favorites!");
      onClose();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const buyers = buyersData?.getUserBuyers || [];

  // Filter out buyers who already have this listing favorited
  const availableBuyers = buyers.filter((buyer) => {
    const alreadyFavorited = favoriteStatus?.buyerFavorites?.some(
      (fav) => fav.buyerId === buyer._id
    );
    return !alreadyFavorited;
  });

  const handleAddToMyFavorites = () => {
    addToAgentFavorites({
      variables: {
        input: {
          listingId,
          agentId,
          companyId,
        },
      },
    });
  };

  const handleAddToBuyerFavorites = () => {
    if (!selectedBuyerId) {
      toast("error", "Please select a buyer");
      return;
    }
    addToBuyerFavorites({
      variables: {
        input: {
          listingId,
          buyerId: selectedBuyerId,
          companyId,
        },
      },
    });
  };

  const isAddedByAgent = favoriteStatus?.isFavoritedByAgent;
  const buyerFavorites = favoriteStatus?.buyerFavorites || [];

  return (
    <Modal
      title="Manage Favorites"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
    >
      <div className="py-4">
        {/* Show current favorites if any */}
        {(isAddedByAgent || buyerFavorites.length > 0) && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-3">Current Favorites</h4>

            {isAddedByAgent && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="w-4 h-4 text-red-500 mr-2"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm">My Favorites</span>
                </div>
                <button
                  onClick={onRemoveAgentFavorite}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            )}

            {buyerFavorites.map((fav) => (
              <div
                key={fav.buyerId}
                className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
              >
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="w-4 h-4 text-red-500 mr-2"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm">{fav.buyerName}</span>
                </div>
                <button
                  onClick={() => onRemoveBuyerFavorite(fav.buyerId)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex mb-4 border-b border-gray-200">
          <button
            onClick={() => setSelectedTab("my")}
            className={`flex-1 py-3 px-4 text-center font-medium border-b-2 transition-colors ${
              selectedTab === "my"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            disabled={isAddedByAgent}
          >
            My Favorites
            {isAddedByAgent && (
              <span className="ml-2 text-xs text-green-600">(Added)</span>
            )}
          </button>
          <button
            onClick={() => setSelectedTab("buyer")}
            className={`flex-1 py-3 px-4 text-center font-medium border-b-2 transition-colors ${
              selectedTab === "buyer"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Buyer's Favorites
          </button>
        </div>

        {/* Tab Content */}
        {selectedTab === "my" ? (
          <div className="text-center py-6">
            {isAddedByAgent ? (
              <p className="text-gray-500">
                This listing is already in your favorites.
              </p>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  Add this listing to your personal favorites list.
                </p>
                <button
                  onClick={handleAddToMyFavorites}
                  disabled={addingAgent}
                  className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  {addingAgent ? (
                    <Spin size="small" className="mr-2" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      className="w-5 h-5 inline-block mr-2"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  Add to My Favorites
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Select a buyer to add this listing to their favorites.
            </p>

            {buyersLoading ? (
              <div className="text-center py-4">
                <Spin />
              </div>
            ) : availableBuyers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                {buyers.length === 0
                  ? "You don't have any buyers yet."
                  : "All your buyers already have this listing favorited."}
              </p>
            ) : (
              <>
                <Select
                  placeholder="Select a buyer"
                  className="w-full mb-4"
                  size="large"
                  value={selectedBuyerId}
                  onChange={setSelectedBuyerId}
                  options={availableBuyers.map((buyer) => ({
                    value: buyer._id,
                    label: `${buyer.firstName} ${buyer.lastName}`,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />

                <button
                  onClick={handleAddToBuyerFavorites}
                  disabled={addingBuyer || !selectedBuyerId}
                  className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  {addingBuyer ? (
                    <Spin size="small" className="mr-2" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      className="w-5 h-5 inline-block mr-2"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  Add to Buyer's Favorites
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FavoriteModal;
